import type { BossId } from '@bioforge/protocol';
import { ADENO, CORONA, ECOLI, GRIPPOZAVR, SPORNITSA } from './mockup.js';
import { boss as draftBoss } from './parts.js';

/**
 * Персонажи макетов «BioForge — игровое окно» (компоненты Microbe, Boss, Logo) — исходные SVG.
 * Боссы без макета (Вибрион-Спрут, Туберкул, Химера) — черновые рисунки из `parts.ts`.
 */

const svg = (viewBox: string, body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" overflow="visible">${body}</svg>`;

/** Микроб по индексу цвета движка: 0 — Коронавирус, 1 — Аденовирус, 2 — Кишечная палочка. */
export const MICROBE_SVG: readonly string[] = [CORONA, ADENO, ECOLI].map((body) =>
  svg('0 0 24 24', body),
);

/** Биоплёнка поверх микроба (у сторожевых — золотая). */
export function armorSvg(gold = false): string {
  const line = gold ? '#FFD86A' : '#B9F0DF';
  const fill = gold ? 'rgba(255, 210, 90, 0.18)' : 'rgba(160, 230, 210, 0.16)';
  return svg(
    '0 0 24 24',
    `<polygon points="12,0.8 21.6,6.4 21.6,17.6 12,23.2 2.4,17.6 2.4,6.4" fill="${fill}" stroke="#120B2A" stroke-width="3.2" stroke-linejoin="round"/><polygon points="12,0.8 21.6,6.4 21.6,17.6 12,23.2 2.4,17.6 2.4,6.4" fill="none" stroke="${line}" stroke-width="1.6" stroke-linejoin="round" stroke-dasharray="3 1.4"/>`,
  );
}

export type BossMood = 'idle' | 'scream';

export function bossSvg(id: BossId, mood: BossMood = 'idle'): string {
  const scream = mood === 'scream';
  if (id === 'spornitsa') return svg('0 0 48 48', SPORNITSA(scream));
  if (id === 'grippozavr') return svg('0 0 48 48', GRIPPOZAVR(scream));
  return svg('0 0 200 200', draftBoss(id));
}

/** Закрытый босс (ещё не встречен): силуэт с вопросом. */
export const LOCKED_BOSS_SVG = svg(
  '0 0 48 48',
  `<circle cx="24" cy="26" r="19" fill="#1A1430" stroke="#4A3C70" stroke-width="2" stroke-dasharray="3 3"/><path d="M18.5 20.5a5.5 5.5 0 1 1 7.6 5.1c-1.4.6-2.1 1.5-2.1 3v1.4" stroke="#6A5A96" stroke-width="3.2" fill="none" stroke-linecap="round"/><circle cx="24" cy="35.5" r="2" fill="#6A5A96"/>`,
);

/** Знак логотипа: медный шестигранник с капсулой. */
export const LOGO_SVG = svg(
  '0 0 24 24',
  `<defs><linearGradient id="logoCopper" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#F7CF8E"/><stop offset="0.45" stop-color="#C27A3E"/><stop offset="1" stop-color="#7A3F1C"/></linearGradient></defs><polygon points="12,1.5 21.1,6.75 21.1,17.25 12,22.5 2.9,17.25 2.9,6.75" fill="url(#logoCopper)" stroke="#120B2A" stroke-width="1.6" stroke-linejoin="round"/><polygon points="12,4.6 18.4,8.3 18.4,15.7 12,19.4 5.6,15.7 5.6,8.3" fill="#0D3040" stroke="#120B2A" stroke-width="0.8" stroke-linejoin="round"/><g transform="rotate(-40 12 12)"><path d="M12 9.2H8.9a2.8 2.8 0 0 0 0 5.6H12z" fill="#9B6BFF"/><path d="M12 9.2h3.1a2.8 2.8 0 0 1 0 5.6H12z" fill="#FFD23A"/><rect x="6.1" y="9.2" width="11.8" height="5.6" rx="2.8" fill="none" stroke="#120B2A" stroke-width="1.1"/><path d="M8.6 10.6h6.2" stroke="#FFFFFF" stroke-opacity="0.7" stroke-width="0.9" stroke-linecap="round"/></g><path d="M19.6 0.6l.7 1.7 1.7.7-1.7.7-.7 1.7-.7-1.7-1.7-.7 1.7-.7z" fill="#FFD23A" stroke="#120B2A" stroke-width="0.6" stroke-linejoin="round"/>`,
);

/**
 * Скины микробов из магазина (компонент «Микроб» макета): колпак и тёмные очки поверх рисунка.
 * Колпак выходит за квадрат микроба, поэтому поле рисунка шире: 32 × 32 вокруг квадрата 24 × 24.
 */
export const HAT_FRAME = 32 / 24;

export function hatSvg(look: 'santa' | 'shades', color: number): string {
  const frame = (body: string) => svg('-4 -4 32 32', body);
  if (look === 'santa') {
    return frame(
      `<path d="M6.2 6.8Q9.4-1.6 19.4-1.2L16.6 5.4Z" fill="#E8344E" stroke="#120B2A" stroke-width="1.2" stroke-linejoin="round"/><path d="M5.6 7.2Q11 4.2 17.2 5.4" stroke="#120B2A" stroke-width="4.2" fill="none" stroke-linecap="round"/><path d="M5.6 7.2Q11 4.2 17.2 5.4" stroke="#FFFFFF" stroke-width="2.6" fill="none" stroke-linecap="round"/><circle cx="19.6" cy="-1.2" r="2" fill="#FFFFFF" stroke="#120B2A" stroke-width="1"/>`,
    );
  }
  // Очки садятся на глаза: у каждого вида они на своей высоте, у палочки — наклонены.
  const eyeY = color === 1 ? 9.8 : 10;
  const left = color === 2 ? 7.6 : 6.8;
  const tilt = color === 2 ? 'rotate(-32 12 12)' : 'rotate(0 12 12)';
  const bridge = `M${left + 5.2} ${eyeY + 1.2}h0.4M${left - 1.2} ${eyeY + 0.8}h1.2M${left + 10.8} ${eyeY + 0.8}h1.2`;
  return frame(
    `<g transform="${tilt}"><path d="${bridge}" stroke="#120B2A" stroke-width="1.2"/><rect x="${left}" y="${eyeY}" width="5.2" height="3.6" rx="1.5" fill="#120B2A"/><rect x="${left + 5.6}" y="${eyeY}" width="5.2" height="3.6" rx="1.5" fill="#120B2A"/></g>`,
  );
}
